import { useState, type FormEvent, type JSX } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { usePostHog } from '@posthog/react';
import { Button, Card, Field, Input, StatusIndicator } from '@viniciomendesr/rayrovisk-design-system';
import { requestSignInLink } from '../api/queries';
import { useSession } from '../auth/SessionProvider';
import { BrandHeader } from './PasswordScreenParts';

export interface SignInScreenProps {
  /** RS-14 is the same submit with a different frame around it. */
  variant?: 'sign-in' | 'resend';
}

export function SignInScreen({ variant = 'sign-in' }: SignInScreenProps): JSX.Element | null {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const posthog = usePostHog();

  const resend = variant === 'resend';
  const location = useLocation();
  const { session, loading } = useSession();
  // Set by the arrival router when the link she just clicked was already spent
  // or past its fifteen minutes. Without it she lands on a plain form and has
  // to guess whether anything went wrong.
  const linkFailed = Boolean((location.state as { linkFailed?: boolean } | null)?.linkFailed);

  // The mirror of RequireSession, and it has to wait the same way: rendering the
  // form before the first session answer arrives asks a signed-in buyer for a
  // new link, and the link she then clicks opens a second session beside the
  // live one.
  if (loading) {
    return null;
  }

  if (session) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/biblioteca'} replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    // The answer is the same whether the address is known or not, and the
    // screen must not undo that: telling her "não encontramos esse e-mail"
    // would turn this form into an account-enumeration oracle (§12.3).
    await requestSignInLink(email).catch(() => undefined);
    setBusy(false);
    setSent(true);
    posthog?.capture('sign_in_link_requested', { variant });
  }

  return (
    <section
      style={{
        minHeight: '100vh',
        background: 'var(--rvk-color-cream-50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(28px, 6vw, 56px) clamp(14px, 5vw, 24px)'
      }}
    >
      <div style={{ width: '100%', maxWidth: 412, display: 'flex', flexDirection: 'column', gap: 22 }}>
        {/* The same header the password screens and the magic-link landing use.
            It was a copy here, and the copy had drifted to its own logo size —
            which is how one surface ended up with a different mark from the
            five beside it. */}
        <BrandHeader />

        <Card layer="functional" elevation="raised" padding="lg">
          {sent ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <StatusIndicator status="live" label="Link enviado" detail="vale 15 minutos" />
              <h1 style={{ margin: 0, font: '400 24px/1.15 var(--rvk-font-expressive)' }}>
                Confira o seu e-mail.
              </h1>
              <p
                style={{
                  margin: 0,
                  font: '400 13.5px/1.55 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Se existir uma compra nesse endereço, o link de entrada chega em instantes. Não
                esqueça de olhar a caixa de promoções.
              </p>
              <Button variant="ghost" size="sm" onClick={() => setSent(false)}>
                Usar outro e-mail
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {linkFailed && (
                <StatusIndicator status="failed" label="Esse link não valia mais" />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <h1 style={{ margin: 0, font: '400 26px/1.15 var(--rvk-font-expressive)' }}>
                  {resend ? 'Reenviar o acesso' : 'Entrar no Roteirovisk'}
                </h1>
                <p
                  style={{
                    margin: 0,
                    font: '400 13.5px/1.55 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-secondary)'
                  }}
                >
                  {linkFailed
                    ? 'O link vale 15 minutos e só abre uma vez. Peça outro aqui, chega na hora.'
                    : resend
                      ? 'A gente reenvia a partir do e-mail da compra, sem passar pelo suporte.'
                      : 'Sem senha. O link chega no seu e-mail e vale 15 minutos.'}
                </p>
              </div>

              <Field label="E-mail da compra" hint="Use o mesmo e-mail que você usou na Hotmart.">
                <Input
                  type="email"
                  required
                  placeholder="voce@email.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Button type="submit" variant="primary" fullWidth loading={busy}>
                {resend ? 'Reenviar link de acesso' : 'Enviar link de acesso'}
              </Button>

              {!resend && (
                <>
                  <div style={{ height: 1, background: 'var(--rvk-border-subtle)' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                    {/* The second door, and it stays second: the link is the
                        default entry and the purchase access e-mail, so a
                        password never sits between a buyer and what she paid
                        for (ADR-0019). */}
                    <Link to="/entrar/senha">
                      <Button variant="ghost" size="sm" type="button">
                        Entrar com senha
                      </Button>
                    </Link>
                    <Link to="/reenviar">
                      <Button variant="ghost" size="sm" type="button">
                        Comprei e o e-mail não chegou
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </form>
          )}
        </Card>

        <p
          style={{
            margin: 0,
            textAlign: 'center',
            font: '400 12px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-text-tertiary)'
          }}
        >
          Um login serve para os dois produtos: o Pacote de Prompts e o Gerador de Prompts e
          Roteiros. O que você comprou aparece dentro.
        </p>
      </div>
    </section>
  );
}
